// utils/trackingParser.js

export const normalizeTrackingData = response => {
  if (!response) return []

  const courier = response?.courier?.toLowerCase()

  // =========================================
  // COMMON STATUS NORMALIZER
  // =========================================

  const normalizeStatus = (status = '', remarks = '') => {
    const s = `${status} ${remarks}`
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

    // Delivery completion
    if (/\b(delivered|successfully delivered|shipment delivered)\b/.test(s)) {
      return 'Delivered'
    }

    if (s.includes('epod copy received')) {
      return 'Pod Received'
    }

    if (
      s.includes('delivery attempted') ||
      s.includes('attempted delivery') ||
      s.includes('undelivered') ||
      s.includes('delivery attempt failed') ||
      s.includes('not delivered')
    ) {
      return 'Delivery Attempted'
    }

    if (s.includes('out for delivery') || s.includes('outscan')) {
      return 'Out for Delivery'
    }

    // Pickup events
    if (s.includes('out for pickup') || s.includes('outscan for pickup')) {
      return 'Out for Pickup'
    }

    if (
      s.includes('arrived at pickup location') ||
      s.includes('reached pickup location')
    ) {
      return 'Arrived at Pickup Location'
    }

    if (
      s.includes('reached at pickup branch') ||
      s.includes('reached pickup branch') ||
      s.includes('arrived at pickup branch')
    ) {
      return 'Reached at Pickup Branch'
    }

    // Branch and facility events
    if (
      s.includes('reached delivery branch') ||
      s.includes('arrived at delivery branch') ||
      s.includes('unloaded at delivery branch')
    ) {
      return 'Reached Delivery Branch'
    }

    if (
      s.includes('arrived at delivery location') ||
      s.includes('reached delivery location')
    ) {
      return 'Arrived at Delivery Location'
    }

    if (
      s.includes('departed from carrier facility') ||
      s.includes('departed from facility') ||
      s.includes('departed facility') ||
      s.includes('shipment dispatched')
    ) {
      return 'Departed from Facility'
    }

    if (
      s.includes('received at hub') ||
      s.includes('inscan at hub') ||
      s.includes('arrived at hub') ||
      s.includes('received at facility') ||
      s.includes('arrived at facility')
    ) {
      return 'Arrived at Hub'
    }

    if (s.includes('reached destination') || s.includes('at destination')) {
      return 'Reached Destination'
    }

    // General shipment lifecycle
    if (
      s.includes('manifest') ||
      s.includes('order confirmed') ||
      s.includes('shipment booked') ||
      s.includes('softdata') ||
      s.includes('created') ||
      s.includes('order created') ||
      s.includes('picked up') ||
      s.includes('pickup completed') ||
      s.includes('successfully picked up')
    ) {
      return 'Manifested'
    }

    if (s.includes('cancel')) return 'Cancelled'

    if (s.includes('delayed') || s.includes('delay')) {
      return 'Delayed'
    }

    if (s.includes('pending') || s.includes('ready for dispatch')) {
      return 'Pending'
    }

    if (
      s.includes('in transit') ||
      s.includes('intransit') ||
      s.includes('road tracking') ||
      s.includes('on the way')
    ) {
      return 'In Transit'
    }

    return status || 'Tracking Update'
  }

  // =========================================
  // DELHIVERY
  // =========================================

  if (courier?.includes('delhivery')) {
    const trackingStates =
      response?.unified_tracking?.data?.[0]?.trackingStates || []

    let normalized = []

    trackingStates.forEach((state, index) => {
      const status = normalizeStatus(state.label)

      const baseObject = {
        tracking_id: index + 1,
        title: status,
        original_title: state.label,
        time: state.date || null, // 👈 IMPORTANT FIX
        location: '',
        description: state.label,
        completed: true,
        active: false
      }

      // PICKED UP
      if (state.label === 'Picked Up') {
        baseObject.location =
          response?.basic_tracking?.origin_branch ||
          response?.basic_tracking?.origin_city ||
          ''

        baseObject.description = 'Order has been successfully picked up'

        normalized.push(baseObject)
      }

      // SCANS AVAILABLE
      else if (Array.isArray(state.scans) && state.scans.length > 0) {
        state.scans.forEach((scan, scanIndex) => {
          normalized.push({
            ...baseObject,
            tracking_id: `${index}${scanIndex}`,
            location: scan?.scannedLocation || scan?.cityLocation || '',
            description: scan?.scanNslRemark || state.label
          })
        })
      }

      // EMPTY STATES (Out for Delivery / Delivered case)
      else {
        normalized.push(baseObject)
      }
    })

    normalized = normalized.reverse()

    if (normalized.length > 0) {
      normalized[0].active = true
      normalized[0].completed = false
    }

    return normalized
  }

  // =========================================
  // INTERNAL TRACKING
  // =========================================

  if (response?.tracking_type === 'internal') {
    const history = response?.data?.[0]?.tracking_history || []

    const normalized = history.map(item => {
      const rawTime = `${item.orderdate || ''} ${item.ordertime || ''}`.trim()

      return {
        tracking_id: item.tracking_id,

        title: normalizeStatus(item.tracking_status),

        original_title: item.tracking_status,

        time: rawTime,

        location: item.location || '',

        description: item.remarks || '',

        completed: true,

        active: false
      }
    })

    normalized.reverse()

    if (normalized.length > 0) {
      normalized[0].active = true
      normalized[0].completed = false
    }

    return normalized
  }

  // =========================================
  // SHREE MARUTI
  // =========================================

  if (courier?.includes('shree')) {
    const statuses = response?.tracking?.statuses || []

    const normalized = statuses.map((item, index) => {
      const time = item.statusTimestamp || item.createdAt || null

      return {
        tracking_id: index + 1,

        title: normalizeStatus(
          item.category || item.status || item.event || item.subcategory
        ),

        original_title:
          item.subcategory || item.category || item.status || item.event,

        time: time ? new Date(time).toLocaleString() : '',
        location: item.location || '',

        _sortTime: time ? new Date(time).getTime() : 0, // 👈 IMPORTANT

        description: item.subcategory || item.status || '',

        completed: true,
        active: false
      }
    })

    // ✅ SORT LATEST FIRST (IMPORTANT FIX)
    normalized.sort((a, b) => b._sortTime - a._sortTime)

    if (normalized.length > 0) {
      normalized[0].active = true
      normalized[0].completed = false
    }

    return normalized
  }

  // =========================================
  // XPRESSBEES
  // =========================================

  if (courier?.includes('xpress')) {
    const tracking = response?.tracking?.data || []

    const normalized = tracking
      .filter(item => {
        const label = (item.label || '').toLowerCase()

        // 🚫 skip unwanted status
        if (label.includes('inscan pending')) return false

        return true
      })
      .map((item, index) => ({
        tracking_id: index + 1,

        title: normalizeStatus(item.label),

        original_title: item.label,

        time: item.shipmentDate,

        location: item.location || '',

        description: `${item.label} - ${item.location}`,

        completed: true,

        active: false
      }))

    normalized.reverse()

    if (normalized.length > 0) {
      normalized[0].active = true
      normalized[0].completed = false
    }

    return normalized
  }

  // =========================================
  // GATI
  // =========================================

  if (courier?.toLowerCase().includes('gati')) {
    const transit = response?.tracking?.data?.details?.[0]?.TRANSIT_DTLS || []
    const details = response?.tracking?.data?.details?.[0]
    if (details?.pod) {
      delete details.pod
    }
    const normalizeLabel = (label = '') => {
      const s = label.toLowerCase().trim()

      if (s.includes('shipment booked')) return 'Manifested'

      if (s.includes('delivered')) return 'Delivered'

      if (s.includes('out for delivery')) return 'Out for Delivery'

      if (
        s.includes('arrived') ||
        s.includes('received') ||
        s.includes('departed')
      ) {
        return 'In Transit'
      }

      return 'In Transit'
    }

    // STEP 1: map raw data
    let cleaned = transit.map(item => ({
      title: normalizeLabel(item.intransitStatus),
      original_title: item.intransitStatus?.trim(),
      time: `${item.intransitDate || ''} ${item.intransitTime || ''}`.trim(),
      location: item.intransitLocation || '',
      description: item.intransitStatus?.trim() || '',
      completed: true,
      active: false
    }))

    // STEP 2: sort by time (IMPORTANT — not reverse)
    cleaned.sort((a, b) => {
      const t1 = new Date(a.time.split(' ').join(' ')).getTime()
      const t2 = new Date(b.time.split(' ').join(' ')).getTime()
      return t2 - t1 // latest first
    })

    // STEP 3: remove duplicates properly
    const seen = new Set()
    cleaned = cleaned.filter(item => {
      const key = `${item.title}-${item.location}-${item.original_title}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    // STEP 4: mark latest as active
    if (cleaned.length > 0) {
      cleaned[0].active = true
      cleaned[0].completed = false
    }

    return cleaned
  }

  // =========================================
  // DTDC
  // =========================================

  if (courier?.toLowerCase().includes('dtdc')) {
    const statuses = response?.tracking?.statuses || []

    // remove POD if accidentally present anywhere
    const header = response?.tracking?.header
    if (header?.pod) delete header.pod

    const normalizeLabel = (label = '') => {
      const s = label.toLowerCase().trim()

      if (s.includes('delivered')) return 'Delivered'
      if (s.includes('out for delivery')) return 'Out for Delivery'
      if (s.includes('in transit') || s.includes('arrived')) return 'In Transit'
      if (s.includes('accepted') || s.includes('picked')) return 'Manifested'
      if (s.includes('shipment booked') || s.includes('softdata'))
        return 'Manifested'
      if (s.includes('pickup')) return 'Manifested'

      return 'In Transit'
    }

    let cleaned = statuses.map((item, index) => {
      const time = item.statusTimestamp || ''

      return {
        tracking_id: index + 1,
        title: normalizeLabel(item.statusDescription),
        original_title: item.statusDescription?.trim(),
        time,
        location: item.actCityName || item.actBranchName || '',
        description:
          item.remarks
            ?.replace(/<[^>]*>/g, '') // remove HTML
            ?.replace(/\|\s*Weight.*$/i, '') // remove weight part
            ?.trim() || item.statusDescription,
        completed: true,
        active: false
      }
    })

    // STEP 1: sort newest first
    cleaned.sort((a, b) => new Date(b.time) - new Date(a.time))

    // STEP 2: remove duplicates (important for DTDC repeated scans)
    const seen = new Set()
    cleaned = cleaned.filter(item => {
      const key = `${item.title}-${item.time}-${item.location}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    // STEP 3: mark latest as active
    if (cleaned.length > 0) {
      cleaned[0].active = true
      cleaned[0].completed = false
    }

    return cleaned
  }

  return []
}
